import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  ToggleButton,
  Tooltip,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  remove as removeNotification,
  add as addNotification,
} from "../../redux-store/notificationsSlice";
import { update as setBreadCrumbs } from "../../redux-store/breadCrumbsSlice";
import TableHeader from "../../components/Page/tableHeader";
import TableSubHeader from "../../components/Page/tableSubHeader";
import ReportReviewStatusBadge from "../../components/Page/reportReviewStatusBadge";
import {
  TableComponent,
  ItemLink,
  EmptyTablePlaceHolder,
} from "../../components/Table/dataGridPro";
import {
  FormatPeriod,
  FormatReviewDate,
} from "./reportUtils";
import { DownloadFile } from "../../utils/file";
import API from "../../api";
import colors from "../../theme/colors";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
`;
const FiltersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  align-items: center;
  padding: 20px;
  > * {
    min-width: 180px !important;
  }
  @media (max-width: 700px) {
    > * {
      width: 100%;
    }
  }
`;
const NeedsReviewOnlyButton = styled(ToggleButton)`
  && {
    display: none;
  }
`;
const NeedsReviewFilterGroup = styled.fieldset`
  position: relative;
  display: flex;
  align-items: center;
  margin: 0;
  height: 40px;
  padding: 0 10px;
  border: 1px solid rgba(0, 0, 0, 0.23);
  border-radius: 4px;
  box-sizing: border-box;
  &:hover {
    border-color: rgba(0, 0, 0, 0.87);
  }
  legend {
    position: absolute;
    top: -9px;
    left: 8px;
    padding: 0 5px;
    background-color: ${colors.white};
    color: rgba(0, 0, 0, 0.6);
    font-size: 12px;
    line-height: 18px;
  }
`;
const NeedsReviewFilter = styled(FormControlLabel)`
  && {
    margin: 0;
    .MuiCheckbox-root {
      padding: 0;
      margin-right: 8px;
    }
  }
`;
const LocationHeader = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  width: 100%;
  min-width: 0;
  padding-right: 16px;
`;
const ReportLoadingCard = styled(Card)`
  min-height: 215px;
  padding: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const EmptyReportsCard = styled(Card)`
  height: 215px;
  padding: 20px;
`;
const ReportAccordion = styled(Accordion)`
  && {
    &::before {
      display: none;
    }
    &.Mui-expanded .location-report-header {
      border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    }
    &:not(:last-of-type) {
      border-bottom: 1px solid rgba(0, 0, 0, 0.12);
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      .MuiAccordionDetails-root > .MuiPaper-root {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    }
  }
`;
const ReportAccordionSummary = styled(AccordionSummary)`
  && {
    flex-direction: row-reverse;
    gap: 12px;
    flex: 1;
    min-width: 0;
    font-size: 18px;
  }
`;
const ReportAccordionDetails = styled(AccordionDetails)`
  && {
    padding: 0;
    > .MuiPaper-root {
      box-shadow: none;
    }
  }
`;
const ReportTable = styled(TableComponent)`
  .MuiDataGrid-toolbar, .MuiDataGrid-toolbarContainer {
    display: none;
  }
  .in-progress {
    color: ${colors.grayDark};
  }
`;
const LocationReportHeader = ({ location, id, "aria-controls": ariaControls }) => (
  <LocationHeader className="location-report-header">
    <ReportAccordionSummary
      id={id}
      aria-controls={ariaControls}
      expandIcon={<ExpandMoreIcon />}
    >
      {location.name}
    </ReportAccordionSummary>
    <ReportReviewStatusBadge
      className={location.needsReviewCount
        ? "alarm"
        : location.completedCount ? "resolved" : ""}
    >
      {location.needsReviewCount
        ? `${location.needsReviewCount} Need Review`
        : location.completedCount
          ? "All Reports Reviewed"
          : "No Completed Reports"}
    </ReportReviewStatusBadge>
  </LocationHeader>
);

const ExportReportButton = ({ organizationId, report }) => {
  const dispatch = useDispatch();
  const [downloading, setDownloading] = useState(false);

  const handlers = {
    download: async () => {
      setDownloading(true);

      try {
        const download = await API.Reports.GetLocationMonthlyReportPdf({
          organizationId,
          locationId: report.locationId,
          reportPeriod: report.period,
        });

        DownloadFile(download);
      } catch (err) {
        console.log(err);
        const id = new Date().getTime();
        dispatch(
          addNotification({
            id,
            severity: "error",
            title: "Error",
            message: err.message || err,
          })
        );
        setTimeout(() => {
          dispatch(removeNotification(id));
        }, 3000);
      } finally {
        setDownloading(false);
      }
    },
  };

  return (
    <Tooltip title="Download PDF">
      <IconButton
        size="small"
        disabled={downloading}
        aria-label={`Download PDF for ${report.locationName}, ${FormatPeriod(report.period)}`}
        onClick={handlers.download}
      >
        <DownloadIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

const OrganizationReportsContainer = () => {
  const { organizationId } = useParams();
  const organization = useSelector((state) => state.organization);
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const currentYear = new Date().getUTCFullYear();
  const [year, setYear] = useState(currentYear);
  const [availableYears, setAvailableYears] = useState([currentYear]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [needsReviewOnly, setNeedsReviewOnly] = useState(false);

  useEffect(() => {
    if (!user.id) return;

    let active = true;
    setLoading(true);
    setLoadError(false);
    setLocations([]);

    const getReports = async () => {
      try {
        const reports = await API.Reports.GetOrganizationReports({
          organizationId,
          year,
        });

        if (!active) return;
        setAvailableYears(reports.availableYears);
        setLocations(reports.locations.map((location) => ({
          ...location,
          reports: location.reports.map((report) => ({
            ...report,
            locationName: location.name,
          })),
        })));
      } catch (err) {
        console.log(err);
        if (!active) return;

        setLoadError(true);
        const id = new Date().getTime();
        dispatch(
          addNotification({
            id,
            severity: "error",
            title: "Error",
            message: err.message || err,
          })
        );
        setTimeout(() => {
          dispatch(removeNotification(id));
        }, 3000);
      } finally {
        if (active) setLoading(false);
      }
    };

    getReports();
    return () => {
      active = false;
    };
  }, [dispatch, organizationId, year, user.id]);

  const visibleLocations = locations.map((location) => {
    const completedReports = location.reports.filter((report) => !report.isInProgress);
    const reportsNeedingReview = completedReports.filter(
      (report) => report.reviewStatus === "needs-review",
    );

    return {
      ...location,
      completedCount: completedReports.length,
      needsReviewCount: reportsNeedingReview.length,
      visibleReports: needsReviewOnly
        ? location.reports.filter(
          (report) => !report.isInProgress && report.reviewStatus === "needs-review",
        )
        : location.reports,
    };
  }).filter((location) => location.visibleReports.length > 0);

  useEffect(() => {
    dispatch(
      setBreadCrumbs([
        {
          title: organization.name || "My Organization",
          link: "/organization",
        },
        { title: "Compliance Reports", link: "/reports" },
      ]),
    );
  }, [dispatch, organization.name]);

  const tableColumns = useMemo(() => [
    {
      field: "period",
      headerName: "Period",
      width: 170,
      renderCell: ({ row }) =>
        row.isInProgress ? FormatPeriod(row.period) : (
          <ItemLink
            to={`/${organizationId}/reports/${row.locationId}/${row.period}`}
          >
            {FormatPeriod(row.period)}
          </ItemLink>
        ),
    },
    {
      field: "status",
      headerName: "Review Status",
      width: 180,
      display: "flex",
      valueGetter: (value, row) =>
        row.isInProgress
          ? "—"
          : row.reviews.length
            ? `${row.reviews.length} Review${row.reviews.length === 1 ? "" : "s"} Recorded`
            : "Needs Review",
      renderCell: ({ row, value }) => row.isInProgress ? value : (
        <ReportReviewStatusBadge className={row.reviews.length ? "resolved" : "alarm"}>
          {value}
        </ReportReviewStatusBadge>
      ),
    },
    {
      field: "tanks",
      headerName: "Tanks",
      width: 80,
      type: "number",
      valueGetter: (value, row) => row.tankCount,
    },
    {
      field: "alarms",
      headerName: "Alarms",
      width: 90,
      type: "number",
      valueGetter: (value, row) => row.alarmCount,
    },
    {
      field: "reviewedBy",
      headerName: "Reviewed By",
      width: 290,
      valueGetter: (value, row) =>
        row.isInProgress
          ? "—"
          : row.reviews.map((review) => review.name).join("; ") ||
            "Not yet reviewed",
    },
    {
      field: "reviewedDate",
      headerName: "Reviewed Date",
      width: 380,
      valueGetter: (value, row) =>
        row.reviews
          .map((review) => FormatReviewDate(review.date))
          .join("; ") || "—",
    },
    {
      field: "export",
      headerName: "Export",
      width: 85,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) =>
        row.isInProgress ? null : (
          <ExportReportButton organizationId={organizationId} report={row} />
        ),
    },
  ], [organizationId]);

  return (
    <PageContainer>
      <Card>
        <TableHeader>
          <div>
            <h2>Compliance Reports</h2>
            <TableSubHeader>
              View and review monthly tank reports for each location. Reports become available after the month ends.
            </TableSubHeader>
          </div>
        </TableHeader>
        <FiltersContainer>
          <TextField
            select
            label="Year"
            size="small"
            value={year}
            onChange={(event) => setYear(Number(event.target.value))}
          >
            {availableYears.map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </TextField>
          <NeedsReviewOnlyButton
            value="needs-review"
            color="primary"
            size="small"
            selected={needsReviewOnly}
            onChange={() => setNeedsReviewOnly((selected) => !selected)}
            aria-label="Needs review only"
          >
            Needs Review Only
          </NeedsReviewOnlyButton>
          <NeedsReviewFilterGroup>
            <legend>Filter</legend>
            <NeedsReviewFilter
              label="Needs Review Only"
              control={
                <Checkbox
                  size="small"
                  checked={needsReviewOnly}
                  onChange={(event) => setNeedsReviewOnly(event.target.checked)}
                />
              }
            />
          </NeedsReviewFilterGroup>
        </FiltersContainer>
      </Card>
      {loading ? (
        <ReportLoadingCard>
          <CircularProgress aria-label="Loading reports" />
        </ReportLoadingCard>
      ) : loadError || !visibleLocations.length ? (
        <EmptyReportsCard>
          <EmptyTablePlaceHolder>
            {loadError
              ? "Unable to load reports"
              : needsReviewOnly
                ? "No reports need review for this year"
                : "No monthly reports for this year"}
          </EmptyTablePlaceHolder>
        </EmptyReportsCard>
      ) : (
        <Card>
          {visibleLocations.map((location) => (
            <ReportAccordion
              key={`${organizationId}-${year}-${location.id}`}
              defaultExpanded={visibleLocations.length === 1}
              disableGutters
              elevation={0}
              slotProps={{ transition: { unmountOnExit: true } }}
            >
              <LocationReportHeader
                location={location}
                id={`location-${location.id}-header`}
                aria-controls={`location-${location.id}-reports`}
              />
              <ReportAccordionDetails>
                <ReportTable
                  columns={tableColumns}
                  disableColumnSorting
                  disableColumnMenu
                  disableColumnFilter
                  rows={location.visibleReports}
                  getRowClassName={({ row }) => row.isInProgress ? "in-progress" : ""}
                  getRowsFunction={async () => location.visibleReports}
                  label={location.name}
                  placeholder={needsReviewOnly
                    ? "No reports need review for this year"
                    : "No monthly reports for this year"}
                  initialState={{
                    pagination: { paginationModel: { pageSize: -1 } },
                    sorting: { sortModel: [{ field: "period", sort: "desc" }] },
                  }}
                />
              </ReportAccordionDetails>
            </ReportAccordion>
          ))}
        </Card>
      )}
    </PageContainer>
  );
};

export default OrganizationReportsContainer;
